import { Edit01Icon, PlusIcon, XIcon } from '@langchain/untitled-ui-icons';
import { TooltipProvider } from '@radix-ui/react-tooltip';

import { useEffect, useRef, useState } from 'react';

import { IconButton, Input, Text, Tooltip } from '@/design-system';
import { cn } from '@/utils/tailwind';

import { Popover, PopoverContent, PopoverTrigger } from '../Popover';

// New PathButton component
function PathButton({
  path,
  onClick,
  leftIcon,
}: {
  path: string;
  onClick: () => void;
  leftIcon?: React.ReactNode;
}) {
  const segments = path.split('.');
  const rootNode = segments[0];
  const leaf = segments[segments.length - 1];
  const hasMiddleSegments = segments.length > 2;

  const ref = useRef<HTMLButtonElement>(null);
  const [isEllipsizedState, setIsEllipsizedState] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const checkOverflow = () => {
      setIsEllipsizedState(element.scrollWidth > element.clientWidth);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [path]);

  return (
    <Tooltip
      title={isEllipsizedState ? path : undefined}
      tooltipClassName="max-w-[500px]"
    >
      <button
        type="button"
        className={cn(
          'flex w-full flex-none items-center truncate rounded-md bg-background px-2 py-1',
          'text-left text-xxs hover:cursor-pointer hover:bg-secondary'
        )}
        onClick={onClick}
        title={path}
        ref={ref}
      >
        {leftIcon && <span className="mr-2">{leftIcon}</span>}
        {isEllipsizedState ? (
          <>
            <span>{rootNode}</span>
            {hasMiddleSegments ? (
              <span className="text-muted-foreground mx-1">…</span>
            ) : segments.length > 1 ? (
              <span className="text-muted-foreground">.</span>
            ) : null}
            {segments.length > 1 && <span>{leaf}</span>}
          </>
        ) : (
          path
        )}
      </button>
    </Tooltip>
  );
}

const VARIABLE_TAG_CLASSNAME = cn(
  'border border-transparent bg-tertiary px-1 text-placeholder',
  'text-xxs transition-all duration-200',
  'inline-flex items-center gap-1 rounded-sm font-mono font-semibold'
);

export function VariableMappingDropdown({
  variableMappingKey,
  variableMappingValue,
  availablePaths,
  onSelect,
  onRename,
  setActiveVariable,
  onRemove,
  uneditableVariables,
  showValidationErrors,
  readOnly,
  isSuggestions,
}: {
  variableMappingKey: string;
  variableMappingValue: string;
  availablePaths: string[];
  onSelect: (key: string, path: string) => void;
  onRename: (oldKey: string, newKey: string) => [boolean, string];
  setActiveVariable: (activeVariable: string) => void;
  onRemove: () => void;
  uneditableVariables: string[];
  showValidationErrors: boolean;
  readOnly?: boolean;
  isSuggestions?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [key, setKey] = useState(variableMappingKey);

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredPaths = availablePaths
    .filter((path) => path.toLowerCase().includes(searchQuery.toLowerCase()))
    .slice(0, 50);

  const [renameError, setRenameError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      setActiveVariable('');
    };
  }, []);

  const isDisabled = uneditableVariables.includes(variableMappingKey);

  const isNonExistingPath = !availablePaths.includes(variableMappingValue);

  const nonExistingTooltipMsg = isSuggestions ? (
    <div className="flex flex-col gap-1">
      <span>Variable path does not exist in suggestions.</span>
      <span>You can still save, but behavior may be unexpected.</span>
    </div>
  ) : (
    <div className="flex flex-col gap-1">
      <span>Selected mapping does not exist in sample data.</span>
      <span>Evaluator might not work as expected</span>
      <span>but you can still save the evaluator.</span>
    </div>
  );

  const needsMapping = !variableMappingValue && !isDisabled;
  const needsMappingTooltipMsg = (
    <div className="flex flex-col gap-1">
      <span>
        {isSuggestions
          ? 'Variable path is required'
          : 'Variable mapping is required'}
      </span>
    </div>
  );

  const handleRenameSubmit = () => {
    if (key !== variableMappingKey) {
      const [success, error] = onRename(variableMappingKey, key);
      if (!success) {
        setRenameError(error);
      } else {
        setRenameError(null);
        setOpen(false);
        setEditing(false);
      }
    } else {
      setEditing(false);
    }
  };

  const renderVariableTag = () => {
    return (
      <div
        className={cn(
          VARIABLE_TAG_CLASSNAME,
          !readOnly && 'hover:cursor-pointer',
          showValidationErrors &&
            needsMapping &&
            !readOnly &&
            'border-error text-error',
          open && 'border-primary bg-primary text-primary',
          'my-0.5 duration-200 animate-in fade-in'
        )}
      >
        <div
          className={cn(
            'mx-0.5 h-1.5 w-1.5 rotate-45 rounded-[1px] bg-[var(--brand-400)]',
            isNonExistingPath && !readOnly && 'bg-warning-strong',
            needsMapping && 'bg-error-strong'
          )}
        ></div>
        <span className="text-xxs">
          {variableMappingValue
            ? variableMappingValue
            : isDisabled || readOnly
            ? variableMappingKey
            : 'Select path'}
        </span>
        {!readOnly && !isDisabled && (
          <IconButton
            icon={XIcon}
            size="sm"
            className="bg-transparent p-0 hover:bg-transparent"
            iconClassName="size-3"
            color="secondary"
            variant="plain"
            label=""
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          />
        )}
      </div>
    );
  };

  return (
    <TooltipProvider>
      <span className="group inline-flex items-center gap-0.5 pl-0.5 pr-1">
        <Popover
          open={open}
          onOpenChange={(open) => {
            setOpen(open);
            if (!open) {
              setActiveVariable('');
            } else {
              setActiveVariable(variableMappingKey);
            }
          }}
        >
          <PopoverTrigger
            className="inline-flex items-center rounded-md"
            disabled={isDisabled || readOnly}
            data-testid={`variable-mapping-dropdown-${variableMappingKey}`}
          >
            {readOnly ? (
              renderVariableTag()
            ) : (
              <Tooltip
                title={
                  isDisabled
                    ? 'This variable is not editable'
                    : needsMapping
                    ? needsMappingTooltipMsg
                    : isNonExistingPath
                    ? nonExistingTooltipMsg
                    : ''
                }
                side="top"
              >
                {renderVariableTag()}
              </Tooltip>
            )}
          </PopoverTrigger>
          <PopoverContent className="p-1" align="start">
            <div className="flex flex-col">
              <div className="flex flex-col gap-1 px-2 pb-2 pt-1">
                {!isSuggestions && (
                  <Text variant="sm" className="flex items-center">
                    Mapping
                    <Tooltip title={renameError} side="top">
                      <div
                        className={cn(
                          VARIABLE_TAG_CLASSNAME,
                          !readOnly && 'hover:cursor-pointer',
                          'mx-1',
                          renameError && 'border-error text-error'
                        )}
                        onClick={() => setEditing((editing) => !editing)}
                      >
                        <div className="mx-0.5 h-1.5 w-1.5 rotate-45 rounded-[1px] bg-[var(--brand-400)]"></div>
                        {editing ? (
                          <input
                            name="variable-mapping-key"
                            className="w-[100px] border-none bg-transparent p-0 text-xxs leading-tight tracking-snug"
                            type="text"
                            value={key}
                            onChange={(e) => {
                              setKey(e.target.value);
                              setRenameError(null);
                            }}
                            onBlur={() => {
                              handleRenameSubmit();
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === 'Enter') {
                                handleRenameSubmit();
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          <>
                            {variableMappingKey
                              ? variableMappingKey
                              : 'Select path'}
                            <IconButton
                              icon={Edit01Icon}
                              size="sm"
                              className="bg-transparent p-0 hover:bg-transparent"
                              iconClassName="size-3"
                              color="secondary"
                              variant="plain"
                              label="Edit variable name"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditing(true);
                              }}
                            />
                          </>
                        )}
                      </div>
                    </Tooltip>
                    to a path...
                  </Text>
                )}
                <Input
                  size="sm"
                  type="text"
                  value={searchQuery}
                  onChange={(mSearchQuery) => setSearchQuery(mSearchQuery)}
                  placeholder="Search or create paths..."
                  autoFocus
                />
              </div>
              <div className="-mx-1 mb-1 bg-tertiary px-2 py-1">
                <Text
                  variant="xs"
                  className="flex items-center gap-1 uppercase text-primary"
                >
                  {isSuggestions
                    ? 'Select path'
                    : 'Map from example or sample run'}
                </Text>
              </div>
              <div
                className="flex max-h-[200px] flex-col gap-2 overflow-y-auto overscroll-contain"
                tabIndex={0}
                onWheel={(e) => {
                  // This is needed because this dropdown doesn't get focus since it's rendered
                  // outside of the react context.
                  e.currentTarget.scrollTop += e.deltaY;
                }}
              >
                {filteredPaths.map((path) => (
                  <PathButton
                    key={path}
                    path={path}
                    onClick={() => {
                      setOpen(false);
                      setActiveVariable('');
                      setSearchQuery('');
                      onSelect(variableMappingKey, path);
                    }}
                  />
                ))}
              </div>
              {searchQuery.length > 0 &&
                filteredPaths.every((path) => path !== searchQuery) && (
                  <PathButton
                    path={searchQuery}
                    onClick={() => {
                      setOpen(false);
                      setActiveVariable('');
                      onSelect(variableMappingKey, searchQuery);
                    }}
                    leftIcon={<PlusIcon className="h-4 w-4" />}
                  />
                )}
            </div>
          </PopoverContent>
        </Popover>
      </span>
    </TooltipProvider>
  );
}
